import { Box, Flex, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import styled from 'styled-components'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { Icon } from 'views/TokenPages/icons'
import { useContractBinding } from '../contract'
import { TYPES, short } from '../config'
import { usePoolPair, useTokenMeta } from '../hooks'
import { Addr, Bar, Countdown, Display, Icon3d, Num, PhasePill, Pill, surface, useNow } from '../ui'
import { competitionPath, legacyPath } from '../routes'
import { LiquidityLock, LockLink, LockPill } from './LockBadge'
import { feeLabel } from './lockCopy'
import TokenAmount from './TokenAmount'
import { EnterButton, TRADE_ANCHOR, enterLabel } from './EnterCta'

export type CompetitionRow = {
  /** a competition of the previous contract (V3): shown with a small "legacy" tag and opened under /legacy */
  legacy?: boolean
  id: number
  creator: string
  pool: string
  projectToken: string
  startTime: number
  endTime: number
  refundAfter: number
  rewardToken: string
  rewardAmount: string
  numberOfWinners: number
  type: string
  phase: string
  status: string
  liquidityLock?: LiquidityLock
}

const CardLink = styled.article<{ $live?: boolean }>`
  ${surface}
  ${({ $live }) => ($live ? 'box-shadow: inset 0 1px 0 rgba(57, 217, 138, 0.45), 0 10px 30px rgba(0, 0, 0, 0.35), 0 0 32px rgba(57, 217, 138, 0.08);' : '')}
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  height: 100%;
  transition: border-color 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
  /* the title link covers the whole card; the lock link sits above it */
  a.stretch::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
  }
`
const Stat = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box style={{ minWidth: 0 }}>
    <Text fontSize="12px" color="textSubtle">{label}</Text>
    <Text bold fontSize="15px" style={{ overflowWrap: 'anywhere' }}>{children}</Text>
  </Box>
)

export default function CompetitionCard({ c }: { c: CompetitionRow }) {
  const { base } = useContractBinding()
  const href = V4_ENABLED ? (c.legacy ? legacyPath(c.id) : competitionPath(c.id)) : `${base}/${c.id}`
  const project = useTokenMeta(c.projectToken, true)
  const pair = usePoolPair(c.pool)
  const now = useNow(30_000)
  const type = TYPES.find((t) => t.key === c.type)
  const pct = now === null ? 0 : ((now - c.startTime) / (c.endTime - c.startTime)) * 100
  const title = project.symbol ?? short(c.projectToken)
  return (
    <CardLink data-testid="v3-card" data-phase={c.phase} $live={c.phase === 'live'}>
      <Flex style={{ gap: 12 }} alignItems="center">
        <TokenLogo address={c.projectToken} symbol={project.symbol} size={56} ring="soft" />
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Link href={href} className="stretch" aria-label={`Open competition #${c.id}, ${title}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Display bold fontSize="20px" lineHeight="24px" style={{ overflowWrap: 'anywhere' }}>{title}</Display>
          </Link>
          <Text fontSize="12px" color="textSubtle">
            #{c.id}{c.legacy ? ' · legacy' : ''} · {pair.dex ? pair.dex.name : 'Pool'}
            {pair.symbol0 && pair.symbol1 ? ` · ${pair.symbol0}/${pair.symbol1}${feeLabel(pair.fee) ? ` ${feeLabel(pair.fee)}` : ''}` : ''}
          </Text>
        </Box>
        <PhasePill phase={c.phase} />
      </Flex>
      <Flex style={{ gap: 8 }} flexWrap="wrap" alignItems="center">
        <Pill $tone="info">{type?.short ?? c.type}</Pill>
        <LockPill lock={c.liquidityLock} pool={c.pool} pair={pair} />
      </Flex>
      <LockLink lock={c.liquidityLock} pool={c.pool} />
      <Flex style={{ gap: 12 }} alignItems="center">
        <Icon3d name="money_bag" size={40} />
        <Box style={{ minWidth: 0 }}>
          <Text fontSize="12px" color="textSubtle">Prize pool</Text>
          <Display bold fontSize="26px" lineHeight="30px" color="gold" style={{ overflowWrap: 'anywhere' }}>
            <TokenAmount raw={c.rewardAmount} token={c.rewardToken} />
          </Display>
          <Text fontSize="13px" color="textSubtle">Top {c.numberOfWinners} share it pro rata to score</Text>
        </Box>
      </Flex>
      {c.phase === 'live' && <Bar $pct={pct} $tone="success" role="progressbar" aria-label="Time elapsed" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} />}
      <Text fontSize="14px" color="textSubtle"><Countdown c={c} /></Text>
      {/* a live competition's primary action, above the card's own link (site review 59 #1): straight to its trade panel */}
      {c.phase === 'live' && (
        <Link href={`${href}#${TRADE_ANCHOR}`} passHref legacyBehavior>
          <EnterButton data-testid="v3-card-cta" style={{ minHeight: 48, fontSize: 16 }}>
            <Icon name="trend" size={18} /> {enterLabel(c.type, title)}
          </EnterButton>
        </Link>
      )}
      <Flex justifyContent="space-between" style={{ gap: 12 }} mt="auto">
        <Stat label="Creator"><Addr>{short(c.creator)}</Addr></Stat>
        <Stat label="Winners"><Num>{c.numberOfWinners}</Num></Stat>
      </Flex>
    </CardLink>
  )
}
